/**
 * Contexte d'authentification ABBUSINESS
 *
 * Stratégie cookie httpOnly :
 *   - Le token Sanctum est stocké dans un cookie httpOnly `ab_token`
 *     (Secure, SameSite=Strict) — jamais dans localStorage.
 *   - Au montage, `GET /auth/me` avec credentials:'include' restaure la
 *     session depuis le cookie si celui-ci est encore valide.
 *   - Les données utilisateur (nom, rôle, etc.) sont stockées dans le state
 *     React — seul le token reste invisible côté JS.
 *   - L'événement 'auth:unauthorized' (dispatché par api.js sur 401) force
 *     une déconnexion propre sans boucle infinie.
 *
 * Exposition :
 *   const { user, isAuth, isLoading, login, logout, refreshUser } = useAuth();
 */

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import api, { profileApi } from '../services/api';

// ── Contexte ──────────────────────────────────────────────────────────────────

const AuthContext = createContext(null);

// ── Provider ──────────────────────────────────────────────────────────────────

/**
 * @param {{ children: React.ReactNode }} props
 */
export function AuthProvider({ children }) {
  /** @type {[import('./types').User|null, Function]} */
  const [user, setUser]         = useState(null);
  const [isLoading, setLoading] = useState(true); // true le temps de vérifier la session
  const [error, setError]       = useState(null);

  // Référence pour éviter une double restauration en StrictMode (React 18)
  const restoredRef = useRef(false);

  // ── Restauration de session au montage ───────────────────────────────────

  const restoreSession = useCallback(async () => {
    try {
      const data = await api.get('/auth/me');
      setUser(data?.user ?? null);
    } catch (err) {
      if (err.status !== 401) {
        setError(err.message);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;
    restoreSession();
  }, [restoreSession]);

  // ── Écoute de l'événement 401 global ────────────────────────────────────

  useEffect(() => {
    /**
     * Dispatché par api.js quand une réponse 401 est reçue.
     * Force la déconnexion locale sans appel réseau supplémentaire
     * (le token est déjà invalide côté serveur).
     */
    function onUnauthorized() {
      setUser(null);
      setLoading(false);
    }

    window.addEventListener('auth:unauthorized', onUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', onUnauthorized);
  }, []);

  // ── Login ────────────────────────────────────────────────────────────────

  /**
   * Connexion de l'utilisateur.
   *
   * Le cookie ab_token est posé par le serveur dans la réponse HTTP ;
   * aucune donnée de token ne transite dans le JSON.
   *
   * @param {string} email
   * @param {string} password
   * @returns {Promise<import('./types').User>}
   */
  const login = useCallback(async (email, password) => {
    setError(null);

    const data = await api.post('/auth/login', { email, password });

    // Navigateur deja reconnu : le serveur retourne directement user + token.
    if (data.token) localStorage.setItem('abbusiness_token', data.token);
    setUser(data.user ?? null);
    return data;   // retourner data complet (requires_2fa inclus si 2FA requis)
  }, []);

  // ── Logout ───────────────────────────────────────────────────────────────

  /**
   * Déconnexion.
   *
   * Appel serveur qui révoque le token Sanctum et supprime le cookie.
   * Le state local est effacé même si la requête échoue.
   */
  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // Erreur réseau : on déconnecte quand même localement
    } finally {
      localStorage.removeItem('abbusiness_token');
      setUser(null);
    }
  }, []);

  // ── Register ─────────────────────────────────────────────────────────────

  /**
   * Inscription d'un nouvel utilisateur.
   *
   * @param {{ name: string, email: string, password: string, password_confirmation: string }} fields
   * @returns {Promise<import('./types').User>}
   */
  const register = useCallback(async (fields) => {
    setError(null);

    const data = await api.post('/auth/register', fields);

    // L'inscription exige une vérification email : le serveur ne retourne
    // pas d'utilisateur ni de token ({message, requires_verification: true}).
    // On n'initialise pas la session ici — loginWithToken() s'en chargera
    // une fois l'email vérifié et le 2FA validé.
    return data;
  }, []);

  // ── Rafraîchissement des données utilisateur ─────────────────────────────

  /**
   * Recharge les infos utilisateur depuis le serveur.
   * Utile après mise à jour du profil.
   *
   * @returns {Promise<void>}
   */
  const refreshUser = useCallback(async () => {
    try {
      const data = await api.get('/auth/me');
      setUser(data?.user ?? null);
    } catch {
      // Session expirée : l'événement auth:unauthorized est déjà géré
    }
  }, []);

  // ── Mise à jour du profil ────────────────────────────────────────────────

  const updateProfile = useCallback(async (data) => {
    const res = await profileApi.update(data);
    setUser(res.user ?? null);
    return res;
  }, []);

  // ── Changement de mot de passe ────────────────────────────────────────────

  const updatePassword = useCallback(async (data) => {
    return await profileApi.updatePassword(data);
  }, []);

  // ── Login avec token (après 2FA, OAuth) ────────────────────────────────────

  // loginWithToken: stocke le token Bearer dans localStorage (lu par api.js).
  const loginWithToken = useCallback((user, token) => {
    if (token) localStorage.setItem('abbusiness_token', token);
    setUser(user);
  }, []);

  // ── Valeur du contexte ───────────────────────────────────────────────────

  /*
   | Reinitialisation du mot de passe.
   |
   | ForgotPassword.jsx et ResetPassword.jsx lisaient ces deux fonctions
   | ici depuis toujours ; elles n'y ont jamais ete. Elles valaient donc
   | undefined, et les appeler levait un TypeError que l'ecran presentait
   | comme « Lien invalide ou expire » — un message decrivant une cause
   | qui n'avait pas eu lieu, et cachant celle qui l'avait.
   |
   | Aucun etat a maintenir ici : ces deux operations ne touchent pas la
   | session en cours. On les expose pour que les ecrans aient un seul
   | endroit ou chercher.
   */
  const forgotPassword = useCallback((email) => api.post('/auth/forgot-password', { email }), []);

  const resetPassword  = useCallback((data)  => api.post('/auth/reset-password', data), []);

  const value = {
    user,
    isAuth:    user !== null,
    isAdmin:   user?.role === 'admin',
    isStaff:   !!user?.is_staff,
    isClient:  !user?.is_staff && user !== null,
    isLoading,
    error,
    login,
    loginWithToken,
    logout,
    register,
    refreshUser,
    updateProfile,
    updatePassword,
    forgotPassword,
    resetPassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────────────────────

/**
 * Accès au contexte d'authentification.
 *
 * @returns {{
 *   user: import('./types').User|null,
 *   isAuth: boolean,
 *   isLoading: boolean,
 *   error: string|null,
 *   login: (email: string, password: string) => Promise<import('./types').User>,
 *   logout: () => Promise<void>,
 *   register: (fields: object) => Promise<import('./types').User>,
 *   refreshUser: () => Promise<void>,
 * }}
 */
export function useAuth() {
  const ctx = useContext(AuthContext);

  if (ctx === null) {
    throw new Error('useAuth() doit être utilisé à l\'intérieur de <AuthProvider>.');
  }

  return ctx;
}
